/**
 * Look-and-feel of the form's custom section navigation — the draggable
 * floating menu (`components/fields/FloatingSectionMenu.tsx`) or the Back/Next
 * stepper bar (`components/fields/SectionStepper.tsx`).
 *
 * `useFloatingMenu` (a bare boolean) says *whether* custom navigation replaces
 * the dropdown / tab strip; this config says WHICH one it is (`mode`) and what
 * it looks like — where the button spawns, how far it is held off the edge, its
 * colour and glyph, how tall the expanded panel is allowed to grow, and which
 * edge the stepper bar sits on.
 *
 * It lives in the form-content envelope (`lib/formContent.ts`) beside
 * `guidedFlow` and `resultPage` — no DB migration, and it rides the existing
 * `/api/forms/share` payload into the offline definition cache for free.
 * `serializeFormContent` drops it when it matches the defaults, so forms that
 * never opened the dialog keep byte-identical content and don't show as dirty.
 *
 * Kept free of React so the server payload builder and tests can import it;
 * the icon-name → component map lives in `components/fields/floating-menu-icons.tsx`.
 */

/** Corner (or mid-edge) of the form surface the button spawns at. */
export type FloatingMenuPosition =
  | "top-left"
  | "top-right"
  | "middle-left"
  | "middle-right"
  | "bottom-left"
  | "bottom-right"

export const FLOATING_MENU_POSITIONS: { value: FloatingMenuPosition; label: string }[] = [
  { value: "top-left", label: "Top left" },
  { value: "top-right", label: "Top right" },
  { value: "middle-left", label: "Middle left" },
  { value: "middle-right", label: "Middle right" },
  { value: "bottom-left", label: "Bottom left" },
  { value: "bottom-right", label: "Bottom right" },
]

/** Allow-listed lucide glyphs — free-text icon names would break the bundle. */
export type FloatingMenuIconName =
  | "list"
  | "menu"
  | "compass"
  | "map"
  | "layers"
  | "clipboard"
  | "grid"
  | "bookmark"

export const FLOATING_MENU_ICON_NAMES: FloatingMenuIconName[] = [
  "list",
  "menu",
  "compass",
  "map",
  "layers",
  "clipboard",
  "grid",
  "bookmark",
]

/**
 * Which chrome the form navigates sections with once `useFloatingMenu` is on.
 *   - `menu`    — the draggable FAB + drill-down panel (the original).
 *   - `stepper` — a Back / "Step N of M" / Next bar pinned to one edge.
 * Both replace the "Page N of M" dropdown and the desktop tab strip.
 */
export type FloatingMenuMode = "menu" | "stepper"

export const FLOATING_MENU_MODES: { value: FloatingMenuMode; label: string; hint: string }[] = [
  {
    value: "menu",
    label: "Floating menu",
    hint: "A draggable button that opens the full list of sections and questions.",
  },
  {
    value: "stepper",
    label: "Stepper",
    hint: "A bar with Back and Next that walks through the sections in order.",
  },
]

/** Which edge of the form surface the stepper bar sticks to. */
export type StepperPlacement = "top" | "bottom"

export const STEPPER_PLACEMENTS: { value: StepperPlacement; label: string }[] = [
  { value: "bottom", label: "Bottom" },
  { value: "top", label: "Top" },
]

export type FloatingMenuStyle = {
  /** Menu or stepper — see `FloatingMenuMode`. */
  mode: FloatingMenuMode
  /** Where the button starts. Respondents can still drag it anywhere. */
  position: FloatingMenuPosition
  /** Button background as `#rrggbb`; the panel's accents derive from it. */
  color: string
  icon: FloatingMenuIconName
  /** Expanded panel's max height, as a percentage of the form surface. */
  panelHeightPercent: number
  /**
   * Gap in px between the button and the edge of the surface it lives on —
   * where it spawns, and how close a drag may push it back to the edge.
   */
  edgePadding: number
  /** Stepper only: which edge the bar sits on. */
  stepperPlacement: StepperPlacement
  /**
   * Stepper only: whether the bar's centre label opens the full section list.
   * Off makes the walk strictly linear — Back and Next are the only ways out of
   * a section, which is what a checklist-style inspection usually wants.
   */
  stepperAllowJump: boolean
}

/** Indigo-600 / LayoutList / half the surface — what the menu shipped with. */
export const DEFAULT_FLOATING_MENU_STYLE: FloatingMenuStyle = {
  mode: "menu",
  position: "top-right",
  color: "#4f46e5",
  icon: "list",
  panelHeightPercent: 50,
  edgePadding: 12,
  stepperPlacement: "bottom",
  stepperAllowJump: true,
}

export const MIN_PANEL_HEIGHT_PERCENT = 25
export const MAX_PANEL_HEIGHT_PERCENT = 90

export const MIN_EDGE_PADDING = 0
export const MAX_EDGE_PADDING = 64

/** Ready-made swatches offered in the builder beside the free-form picker. */
export const FLOATING_MENU_COLOR_PRESETS: { value: string; label: string }[] = [
  { value: "#4f46e5", label: "Indigo" },
  { value: "#2563eb", label: "Blue" },
  { value: "#0d9488", label: "Teal" },
  { value: "#16a34a", label: "Green" },
  { value: "#ea580c", label: "Orange" },
  { value: "#e11d48", label: "Rose" },
  { value: "#7c3aed", label: "Violet" },
  { value: "#334155", label: "Slate" },
]

const HEX_RE = /^#[0-9a-f]{6}$/i

/** `#abc` → `#aabbcc`; anything not a hex colour returns null. */
export function normalizeHexColor(raw: unknown): string | null {
  if (typeof raw !== "string") return null
  const value = raw.trim()
  const expanded = /^#[0-9a-f]{3}$/i.test(value)
    ? `#${value[1]}${value[1]}${value[2]}${value[2]}${value[3]}${value[3]}`
    : value
  return HEX_RE.test(expanded) ? expanded.toLowerCase() : null
}

function channel(v: number): number {
  const s = v / 255
  return s <= 0.04045 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4
}

/** WCAG relative luminance of a `#rrggbb` colour. */
export function relativeLuminance(hex: string): number {
  const value = normalizeHexColor(hex) ?? DEFAULT_FLOATING_MENU_STYLE.color
  const r = parseInt(value.slice(1, 3), 16)
  const g = parseInt(value.slice(3, 5), 16)
  const b = parseInt(value.slice(5, 7), 16)
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b)
}

/**
 * Foreground for the button's glyph: white on dark backgrounds, near-black on
 * light ones. Authors pick any colour they like, so the contrast (WCAG 1.4.11,
 * and 1.4.3 for the glyph) has to be derived rather than hard-coded to white.
 */
export function readableTextOn(hex: string): string {
  return relativeLuminance(hex) > 0.45 ? "#111827" : "#ffffff"
}

/** The chosen colour at ~10% alpha — used to tint the active row. */
export function tintOf(hex: string): string {
  const value = normalizeHexColor(hex) ?? DEFAULT_FLOATING_MENU_STYLE.color
  return `${value}1a`
}

function clampPercent(raw: unknown): number {
  const n = typeof raw === "number" && Number.isFinite(raw) ? Math.round(raw) : NaN
  if (Number.isNaN(n)) return DEFAULT_FLOATING_MENU_STYLE.panelHeightPercent
  return Math.min(MAX_PANEL_HEIGHT_PERCENT, Math.max(MIN_PANEL_HEIGHT_PERCENT, n))
}

function clampPadding(raw: unknown): number {
  const n = typeof raw === "number" && Number.isFinite(raw) ? Math.round(raw) : NaN
  if (Number.isNaN(n)) return DEFAULT_FLOATING_MENU_STYLE.edgePadding
  return Math.min(MAX_EDGE_PADDING, Math.max(MIN_EDGE_PADDING, n))
}

/**
 * Lenient parse of the envelope's `floatingMenuStyle` key. Returns undefined
 * when absent/malformed so an untouched form serializes without the key; every
 * individual field falls back to its default, so a partial object is fine.
 */
export function parseFloatingMenuStyle(raw: unknown): FloatingMenuStyle | undefined {
  if (!raw || typeof raw !== "object") return undefined
  const obj = raw as Record<string, unknown>
  const position = FLOATING_MENU_POSITIONS.some((p) => p.value === obj.position)
    ? (obj.position as FloatingMenuPosition)
    : DEFAULT_FLOATING_MENU_STYLE.position
  const icon = FLOATING_MENU_ICON_NAMES.includes(obj.icon as FloatingMenuIconName)
    ? (obj.icon as FloatingMenuIconName)
    : DEFAULT_FLOATING_MENU_STYLE.icon
  const mode = FLOATING_MENU_MODES.some((m) => m.value === obj.mode)
    ? (obj.mode as FloatingMenuMode)
    : DEFAULT_FLOATING_MENU_STYLE.mode
  const stepperPlacement = STEPPER_PLACEMENTS.some((p) => p.value === obj.stepperPlacement)
    ? (obj.stepperPlacement as StepperPlacement)
    : DEFAULT_FLOATING_MENU_STYLE.stepperPlacement

  return {
    mode,
    position,
    color: normalizeHexColor(obj.color) ?? DEFAULT_FLOATING_MENU_STYLE.color,
    icon,
    panelHeightPercent: clampPercent(obj.panelHeightPercent),
    edgePadding: clampPadding(obj.edgePadding),
    stepperPlacement,
    stepperAllowJump:
      typeof obj.stepperAllowJump === "boolean"
        ? obj.stepperAllowJump
        : DEFAULT_FLOATING_MENU_STYLE.stepperAllowJump,
  }
}

/** Fill in every unset field — the single place render code resolves a style. */
export function resolveFloatingMenuStyle(
  style: FloatingMenuStyle | null | undefined,
): FloatingMenuStyle {
  if (!style) return DEFAULT_FLOATING_MENU_STYLE
  return {
    mode: style.mode ?? DEFAULT_FLOATING_MENU_STYLE.mode,
    position: style.position ?? DEFAULT_FLOATING_MENU_STYLE.position,
    color: normalizeHexColor(style.color) ?? DEFAULT_FLOATING_MENU_STYLE.color,
    icon: style.icon ?? DEFAULT_FLOATING_MENU_STYLE.icon,
    panelHeightPercent: clampPercent(style.panelHeightPercent),
    edgePadding: clampPadding(style.edgePadding),
    stepperPlacement: style.stepperPlacement ?? DEFAULT_FLOATING_MENU_STYLE.stepperPlacement,
    stepperAllowJump: style.stepperAllowJump ?? DEFAULT_FLOATING_MENU_STYLE.stepperAllowJump,
  }
}

/** True when nothing has been customised, so the key can be dropped on save. */
export function isDefaultFloatingMenuStyle(style: FloatingMenuStyle | undefined): boolean {
  if (!style) return true
  const resolved = resolveFloatingMenuStyle(style)
  return (
    resolved.mode === DEFAULT_FLOATING_MENU_STYLE.mode &&
    resolved.position === DEFAULT_FLOATING_MENU_STYLE.position &&
    resolved.color === DEFAULT_FLOATING_MENU_STYLE.color &&
    resolved.icon === DEFAULT_FLOATING_MENU_STYLE.icon &&
    resolved.panelHeightPercent === DEFAULT_FLOATING_MENU_STYLE.panelHeightPercent &&
    resolved.edgePadding === DEFAULT_FLOATING_MENU_STYLE.edgePadding &&
    resolved.stepperPlacement === DEFAULT_FLOATING_MENU_STYLE.stepperPlacement &&
    resolved.stepperAllowJump === DEFAULT_FLOATING_MENU_STYLE.stepperAllowJump
  )
}
